import React, { useCallback } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Ionicons } from "@expo/vector-icons";
import type RootStackParamList from "../navigation/RootStackParamList";
import LogoMark from "../assets/LogoMark";
import { signOut } from "../services/userService";
import { triggerHeaderMenuLogout } from "../components/AppHeader/headerMenuActions";
import useHeaderTopPadding from "../components/AppHeader/useHeaderTopPadding";
import { useAppSelector } from "../redux/store";
import useInbox from "../hooks/useInbox";

export default function AppHeaderMenuScreen() {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const isAdmin = useAppSelector((state) => state.user.admin);
  const { unreadCount } = useInbox();
  // RN's SafeAreaView is a no-op on Android, so the logo and close button sat
  // under the status bar. Use the same top padding as the app header so they
  // line up with it (#117).
  const headerTopPadding = useHeaderTopPadding();
  const { bottom: bottomInset } = useSafeAreaInsets();

  const handleClose = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  // replace() like the admin items: going back and then pushing Profile ran two
  // transitions back to back, which felt like a freeze on Android (#117).
  const handleProfile = useCallback(() => {
    navigation.replace("Profile");
  }, [navigation]);

  const handleInbox = useCallback(() => {
    navigation.replace("Inbox");
  }, [navigation]);

  const handleAdminUsers = useCallback(() => {
    navigation.replace("AdminUsers");
  }, [navigation]);

  const handleAdminConversations = useCallback(() => {
    navigation.replace("AdminConversations");
  }, [navigation]);

  const handleAdminSignupFunnel = useCallback(() => {
    navigation.replace("AdminSignupFunnel");
  }, [navigation]);

  const handleLogout = useCallback(async () => {
    navigation.goBack();
    const handled = triggerHeaderMenuLogout();
    if (handled) return;
    const result = await signOut();
    if (!result.success) {
      console.error("Failed to sign out:", result.error);
    }
  }, [navigation]);

  return (
    <View
      style={[
        styles.screen,
        { paddingTop: headerTopPadding, paddingBottom: 16 + bottomInset },
      ]}
    >
      <View style={styles.header}>
        <LogoMark width={40} height={40} />
        <Pressable
          onPress={handleClose}
          hitSlop={12}
          accessibilityRole="button"
          accessibilityLabel="Close menu"
          style={styles.closeButton}
        >
          <Ionicons name="close" size={22} color="#3a6fb1" />
        </Pressable>
      </View>

      <View style={styles.menuCard}>
        <Pressable style={styles.menuItem} onPress={handleProfile}>
          <Ionicons name="person-circle-outline" size={18} color="#fff" />
          <Text style={styles.menuText}>My profile</Text>
        </Pressable>
        <View style={styles.divider} />
        <Pressable style={styles.menuItem} onPress={handleInbox}>
          <Ionicons name="notifications-outline" size={18} color="#fff" />
          <Text style={styles.menuText}>Notifications</Text>
          {unreadCount > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </Text>
            </View>
          )}
        </Pressable>
        {isAdmin && (
          <>
            <View style={styles.divider} />
            <Pressable style={styles.menuItem} onPress={handleAdminUsers}>
              <Ionicons name="mail-outline" size={18} color="#fff" />
              <Text style={styles.menuText}>User emails</Text>
            </Pressable>
            <View style={styles.divider} />
            <Pressable
              style={styles.menuItem}
              onPress={handleAdminConversations}
            >
              <Ionicons name="stats-chart-outline" size={18} color="#fff" />
              <Text style={styles.menuText}>Conversation attendance</Text>
            </Pressable>
            <View style={styles.divider} />
            <Pressable
              style={styles.menuItem}
              onPress={handleAdminSignupFunnel}
            >
              <Ionicons name="funnel-outline" size={18} color="#fff" />
              <Text style={styles.menuText}>Signup funnel</Text>
            </Pressable>
          </>
        )}
        <View style={styles.divider} />
        <Pressable style={styles.menuItem} onPress={handleLogout}>
          <Ionicons name="log-out-outline" size={18} color="#fff" />
          <Text style={styles.menuText}>Logout</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#F6F7F9",
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  // Same geometry as AppHeaderView (24 horizontal incl. screen padding, 14
  // vertical) so the logo and close button line up with the logo and menu.
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 14,
  },
  closeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  menuCard: {
    flex: 1,
    backgroundColor: "#3a6fb1",
    borderRadius: 22,
    paddingVertical: 12,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  menuText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  divider: {
    height: 1,
    marginHorizontal: 16,
    backgroundColor: "rgba(255, 255, 255, 0.35)",
  },
  badge: {
    minWidth: 20,
    paddingHorizontal: 6,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#EF4444",
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "700",
  },
});
